/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: WebSocket Implementation
 * PURPOSE: Reactive WebSocket state with message queue and viewer tracking
 *
 * NEW PATTERNS IMPLEMENTED:
 * - State Machine: Reactive state ($state) for WebSocket connection state
 * - Message Queue: Priority-based queuing for offline message handling (100 messages max)
 * - Viewer State: Reactive tracking of page viewers across namespaces
 * - Transport Management: Clean separation between transport and state layers
 *
 * KEY FEATURES:
 * - Priority message queue (critical > high > normal > low) with automatic retry
 * - Queue flush on reconnect to send pending messages
 * - Viewer helpers for namespace-based viewer tracking
 * - SSR-safe initialization (browser-only transport creation)
 *
 * RELATED FILES:
 * - src/lib/services/websocket/WebSocketTransport.ts
 * - src/lib/services/websocket/websocket.actions.svelte.ts
 */

/**
 * websocket.state.svelte.ts - Svelte 5 Runes WebSocket State
 *
 * Stage F+1: Complete Migration (2025-10-01)
 *
 * Phase 2: Activated with WebSocketTransport integration
 *
 * Architecture (New):
 * - WebSocketTransport.ts = Pure transport layer (connection only)
 * - websocket.state.svelte.ts = Reactive state (this file) - ACTIVATED
 * - websocket.actions.svelte.ts = Business logic and message routing
 *
 * Key Changes:
 * - Integrated with WebSocketTransport (event-based)
 * - $state for reactive WebSocket state
 * - $derived for computed table stores
 * - Transport lifecycle managed here
 * - Message routing delegated to actions layer
 */

import { browser } from '$app/environment';
import type { TableItem, WebSocketState } from '$lib/types/websocket';
import { WebSocketTransport, type TransportOptions } from './WebSocketTransport';
import { WEBSOCKET_CONFIG } from '$lib/config/websocketConfig';
import { createLogger } from '$lib/utils/logger';
import { SvelteSet } from 'svelte/reactivity';
import { connectionState } from '$lib/state/index.svelte';
import { eventBus } from '$lib/core/services/EventBusService';
import {
	recoverySignalAuthInvalid,
	notifyRecoveryWsOpen,
	notifyRecoveryWsClose,
	notifyRecoveryWsProlongedFailure
} from '$lib/state/connection-recovery.state.svelte';

const log = createLogger('ws-state');

/**
 * Message priority levels for queue management
 */
export type MessagePriority = 'critical' | 'high' | 'normal' | 'low';

/**
 * Priority ordering for queue sorting
 */
const PRIORITY_ORDER: Record<MessagePriority, number> = {
	critical: 0,
	high: 1,
	normal: 2,
	low: 3
};

/**
 * Queued message structure
 */
interface QueuedMessage {
	data: any;
	priority: MessagePriority;
	timestamp: number;
	retries: number;
	maxRetries: number;
}

/**
 * Message Queue for handling offline scenarios
 *
 * Purpose: Prevents data loss when WebSocket is disconnected or reconnecting
 *
 * Features:
 * - Priority-based queuing (critical > high > normal > low)
 * - Automatic retry with configurable max attempts
 * - Queue size limits to prevent memory exhaustion
 * - Flush on reconnect to send pending messages
 *
 * Use Cases:
 * 1. User updates status → Network drops → Status update queued
 * 2. User submits form → WebSocket reconnecting → Data queued
 * 3. Batch operations → Connection drops → Operations queued
 */
class MessageQueue {
	private queue: QueuedMessage[] = [];
	private readonly MAX_QUEUE_SIZE = 100;

	/**
	 * Add message to queue
	 *
	 * If queue is full, removes lowest priority oldest message
	 */
	enqueue(data: any, priority: MessagePriority = 'normal'): void {
		// Check queue size limit
		if (this.queue.length >= this.MAX_QUEUE_SIZE) {
			log.warn(`Message queue full (${this.MAX_QUEUE_SIZE}), removing lowest priority message`);

			// Sort by priority (lowest first) and timestamp (oldest first)
			this.queue.sort((a, b) => {
				const priorityDiff = PRIORITY_ORDER[b.priority] - PRIORITY_ORDER[a.priority];
				if (priorityDiff !== 0) return priorityDiff;
				return b.timestamp - a.timestamp;
			});

			// Remove lowest priority oldest message
			const removed = this.queue.pop();
			log.warn('Dropped message from queue:', {
				priority: removed?.priority,
				timestamp: removed?.timestamp
			});
		}

		// Add new message to queue
		const maxRetries = priority === 'critical' ? 5 : priority === 'high' ? 3 : 2;

		this.queue.push({
			data,
			priority,
			timestamp: Date.now(),
			retries: 0,
			maxRetries
		});

		log.info(`Message queued (priority: ${priority}, queue size: ${this.queue.length})`);
	}

	/**
	 * Flush all queued messages
	 *
	 * Sorts by priority and attempts to send each message
	 * Failed messages are re-queued if under max retries
	 */
	flush(sendFn: (data: any) => boolean): void {
		if (this.queue.length === 0) {
			log.debug('No messages to flush');
			return;
		}

		log.info(`Flushing ${this.queue.length} queued messages`);

		// Sort by priority (highest first)
		this.queue.sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]);

		const failedMessages: QueuedMessage[] = [];
		let successCount = 0;
		let failedCount = 0;

		while (this.queue.length > 0) {
			const message = this.queue.shift()!;

			if (sendFn(message.data)) {
				successCount++;
			} else {
				message.retries++;

				if (message.retries < message.maxRetries) {
					failedMessages.push(message);
					log.warn(`Message send failed, retry ${message.retries}/${message.maxRetries}`, {
						priority: message.priority
					});
				} else {
					failedCount++;
					log.error('Message dropped after max retries:', {
						priority: message.priority,
						retries: message.retries,
						data: message.data
					});
				}
			}
		}

		// Re-queue failed messages for next flush
		this.queue = failedMessages;

		log.info(
			`Queue flush complete: ${successCount} sent, ${failedCount} dropped, ${this.queue.length} re-queued`
		);
	}

	/**
	 * Clear all queued messages
	 */
	clear(): void {
		const count = this.queue.length;
		this.queue = [];
		log.info(`Cleared ${count} messages from queue`);
	}

	/**
	 * Get current queue size
	 */
	size(): number {
		return this.queue.length;
	}
}

/**
 * Global message queue instance
 */
const messageQueue = new MessageQueue();

/**
 * Table store type for derived table data
 */
export interface TableStore {
	data: TableItem[];
	lastUpdated: number | null;
	isSubscribed: boolean;
}

/**
 * Viewer data types
 */
export interface ViewerData {
	[userId: string]: {
		display_name: string;
		started_at: string;
	};
}

export interface ViewerState {
	[namespace: string]: {
		[itemId: string]: ViewerData;
	};
}

/**
 * Transport instance (lazy initialized)
 */
let transport: WebSocketTransport | null = null;

/**
 * Message handler callback (set by actions layer)
 */
let messageHandler: ((data: any) => void) | null = null;

/**
 * Buffer for messages that arrive before the actions layer registers its handler.
 * Replayed in order once registerMessageHandler() is called.
 */
let earlyMessages: any[] = [];

/**
 * Main WebSocket state (reactive with $state)
 * Safe for SSR - initialized only in browser context
 */
export const websocketState = $state<WebSocketState>({
	connected: false,
	tables: {},
	subscriptions: new SvelteSet<string>(),
	error: null,
	lastUpdated: {}
});

/**
 * Viewer state (reactive with $state)
 */
export const viewerState = $state<ViewerState>({});

/**
 * Get or create transport instance
 */
function getTransport(): WebSocketTransport {
	if (!transport) {
		if (!browser) {
			throw new Error('Cannot create WebSocket transport in SSR context');
		}

		// Get auth token from sessionStorage (Phase 3 migration)
		const authToken = sessionStorage.getItem('token');

		const options: TransportOptions = {
			url: WEBSOCKET_CONFIG.url,
			authToken: authToken || undefined,
			authType: 'Bearer',
			heartbeatInterval: WEBSOCKET_CONFIG.heartbeatInterval,
			maxReconnectAttempts: WEBSOCKET_CONFIG.maxReconnectAttempts,
			baseDelay: WEBSOCKET_CONFIG.reconnectDelay
		};

		transport = new WebSocketTransport(options);

		// Wire transport events to state updates
		transport.on('open', handleTransportOpen);
		transport.on('close', handleTransportClose);
		transport.on('error', handleTransportError);
		transport.on('message', handleTransportMessage);
		transport.on('reconnecting', handleTransportReconnecting);
		transport.on('reconnected', handleTransportReconnected);
		transport.on('maxReconnectFailed', handleMaxReconnectFailed);
		transport.on('prolongedFailure', () => {
			log.error('Prolonged WS failure (2min) — signaling recovery machine');
			// Direct dispatch to the recovery machine. Previously this
			// emitted a `ws:prolonged-failure` window event that nobody
			// reliably listened to; now the machine is the single owner.
			notifyRecoveryWsProlongedFailure();
		});

		log.info('WebSocket transport created and wired to state');
	}

	return transport;
}

/**
 * Handle transport open event
 */
function handleTransportOpen(): void {
	log.info('Transport connected');
	websocketState.connected = true;
	websocketState.error = null;

	// Sync to global connectionState for UI components
	connectionState.connected = true;
	connectionState.connecting = false;
	connectionState.readyForData = true;
	connectionState.error = null;

	// Direct signal to the recovery machine. WS_OPEN starts the
	// "initial data" budget — the machine waits for a fresh snapshot
	// before transitioning to CONNECTED.
	notifyRecoveryWsOpen();
}

/**
 * Handle transport close event
 *
 * IMPORTANT: Do NOT treat every abnormal close as an auth failure.
 * Network loss, server restart, proxy timeouts — all cause abnormal
 * closes but have nothing to do with the token. Previously this
 * handler fired `auth:refresh-token-needed` on every non-1000 close,
 * which caused needless token refresh cascades on every network blip.
 *
 * Only close codes that the backend uses to SIGNAL auth failure
 * should trigger token refresh. For AMOS:
 *   - 4001: Unauthorized (invalid token)
 *   - 4003: Forbidden (permission revoked)
 *
 * All other close codes are transport-level events handled by the
 * reconnect loop and, via observers, the recovery machine.
 */
function handleTransportClose(event: CloseEvent): void {
	log.info(`Transport closed: ${event.code} ${event.reason}`);
	websocketState.connected = false;

	// Sync to global connectionState for UI components
	connectionState.connected = false;
	connectionState.readyForData = false;

	// Only definitive auth-failure close codes trigger auth recovery.
	// The machine transitions directly to LOGIN_REQUIRED — no intermediate
	// "try a refresh and maybe log out" scramble.
	if (event.code === 4001) {
		log.warn('WebSocket closed with 4001 — definitive auth failure');
		recoverySignalAuthInvalid('token_expired');
		return;
	}
	if (event.code === 4003) {
		log.warn('WebSocket closed with 4003 — permission revoked');
		recoverySignalAuthInvalid('permission_revoked');
		return;
	}
	// Normal/abnormal transport close (1000, 1001, 1006, 1011, etc.).
	// Direct signal to the recovery machine so it can transition to
	// RECONNECTING if we were previously CONNECTED.
	notifyRecoveryWsClose();
}

/**
 * Handle transport error event
 */
function handleTransportError(error: Event): void {
	log.error('Transport error:', error);
	websocketState.error = new Error('Connection error');
}

/**
 * Handle transport message event
 */
function handleTransportMessage(data: any): void {
	// Sync last message time to global connectionState
	connectionState.lastMessageTime = new Date();

	// Delegate message handling to actions layer
	if (messageHandler) {
		messageHandler(data);
	} else {
		// Buffer early messages instead of dropping them
		earlyMessages.push(data);
		log.info(`No message handler yet, buffered message (${earlyMessages.length} pending):`, data.type);
	}
}

/**
 * Handle transport reconnecting event
 */
function handleTransportReconnecting(attempt: number): void {
	log.info(`Reconnecting (attempt ${attempt})...`);
	websocketState.error = new Error(`Reconnecting (attempt ${attempt})...`);

	// Sync to global connectionState for UI components
	connectionState.reconnecting = true;
	connectionState.reconnectAttempts = attempt;
}

/**
 * Handle transport reconnected event
 */
function handleTransportReconnected(): void {
	log.info('Transport reconnected');
	websocketState.error = null;

	// Sync to global connectionState for UI components
	connectionState.reconnecting = false;
	connectionState.reconnectAttempts = 0;

	// Flush queued messages on reconnect
	// This sends any messages that were queued while offline
	if (messageQueue.size() > 0) {
		log.info(`Flushing ${messageQueue.size()} queued messages after reconnect`);
		messageQueue.flush((data) => transport?.send(data) ?? false);
	}

	// Notify data services so they can re-request fresh data
	eventBus.emit('websocket:reconnected', { timestamp: Date.now() });
}

/**
 * Handle max reconnect failed event
 */
function handleMaxReconnectFailed(): void {
	log.error('Max reconnect attempts reached');
	websocketState.error = new Error('Connection lost - please refresh the page');
	websocketState.connected = false;

	// Sync ALL mirrored fields in connectionState so UI is fully consistent
	connectionState.connected = false;
	connectionState.connecting = false;
	connectionState.readyForData = false;
	connectionState.reconnecting = false;
	connectionState.reconnectAttempts = 0;
	connectionState.error = new Error('Connection lost - please refresh the page');
}

/**
 * Get table store data (Svelte 5 pattern)
 * Returns an object with getters that access reactive state
 *
 * @param tableName - Name of the table to get data for
 * @returns TableStore with reactive getters
 */
export function getTableStore(tableName: string): TableStore {
	// Return object with getters that access reactive state
	return {
		get data() {
			return websocketState.tables[tableName] || [];
		},
		get lastUpdated() {
			return websocketState.lastUpdated[tableName] ?? null;
		},
		get isSubscribed() {
			return websocketState.subscriptions.has(tableName);
		}
	};
}

/**
 * Check if WebSocket is connected
 */
export function isConnected(): boolean {
	return websocketState.connected;
}

/**
 * Check if subscribed to a specific table
 */
export function isSubscribedToTable(tableName: string): boolean {
	return websocketState.subscriptions.has(tableName);
}

/**
 * Viewer state helpers
 */
export const viewerHelpers = {
	/**
	 * Update viewers for a specific namespace and item
	 */
	updateViewers(namespace: string, itemId: string, viewerData: ViewerData) {
		viewerState[namespace] = {
			...viewerState[namespace],
			[itemId]: viewerData
		};
	},

	/**
	 * Remove a viewer from a specific item
	 */
	removeViewer(namespace: string, itemId: string, userId: string) {
		const namespaceData = viewerState[namespace];
		if (!namespaceData || !namespaceData[itemId]) return;

		const { [userId]: _, ...remainingViewers } = namespaceData[itemId];

		viewerState[namespace] = {
			...namespaceData,
			[itemId]: remainingViewers
		};
	},

	/**
	 * Get viewers for a specific item
	 */
	getViewers(namespace: string, itemId: string): ViewerData {
		return viewerState[namespace]?.[itemId] || {};
	},

	/**
	 * Clear all viewers in a namespace
	 */
	clearNamespace(namespace: string) {
		const { [namespace]: _, ...rest } = viewerState;
		Object.assign(viewerState, rest);
	}
};

/**
 * SSR Safety Check
 * Returns true if running in browser context
 */
export function isBrowser(): boolean {
	return browser;
}

/**
 * Transport control functions (exported for actions layer)
 */

/**
 * Register message handler (called by actions layer)
 */
export function registerMessageHandler(handler: (data: any) => void): void {
	messageHandler = handler;
	log.info('Message handler registered');

	// Replay any messages that arrived before the handler was registered
	if (earlyMessages.length > 0) {
		log.info(`Replaying ${earlyMessages.length} buffered messages`);
		const buffered = earlyMessages;
		earlyMessages = [];
		for (const msg of buffered) {
			handler(msg);
		}
	}
}

/**
 * Connect to WebSocket server
 */
export async function connect(): Promise<void> {
	if (!browser) {
		log.warn('Cannot connect: not in browser environment');
		return;
	}

	const t = getTransport();
	await t.connect();
}

/**
 * Disconnect from WebSocket server
 */
export function disconnect(preventReconnect: boolean = false): void {
	if (!transport) {
		log.warn('No transport instance to disconnect');
		return;
	}

	transport.disconnect(preventReconnect);
}

/**
 * Send message through WebSocket with queue support
 *
 * Enhancements:
 * - Queues messages when disconnected (prevents data loss)
 * - Supports priority levels for message ordering
 * - Automatic retry on reconnect
 *
 * @param data - Message data to send
 * @param priority - Message priority ('critical' | 'high' | 'normal' | 'low')
 * @returns true if sent immediately, false if queued
 */
export function send(data: any, priority: MessagePriority = 'normal'): boolean {
	if (!transport) {
		log.warn('Cannot send: no transport instance, queueing message');
		messageQueue.enqueue(data, priority);
		return false;
	}

	if (!transport.isConnected()) {
		log.warn('Cannot send: not connected, queueing message');
		messageQueue.enqueue(data, priority);
		return false;
	}

	// Connected - send immediately
	const sent = transport.send(data);

	if (!sent) {
		// Send failed - queue for retry
		log.warn('Send failed, queueing message for retry');
		messageQueue.enqueue(data, priority);
		return false;
	}

	return true;
}

/**
 * Update authentication credentials
 */
export function updateAuth(authToken: string, authType: string): void {
	if (!transport) {
		log.warn('Cannot update auth: no transport instance');
		return;
	}

	transport.updateAuth(authToken, authType);
}

/**
 * Cleanup transport resources
 */
export function cleanup(): void {
	if (transport) {
		transport.cleanup();
		transport = null;
		messageHandler = null;
		log.info('Transport cleaned up');
	}

	// Sync ALL mirrored fields so UI knows we're fully disconnected
	connectionState.connected = false;
	connectionState.connecting = false;
	connectionState.readyForData = false;
	connectionState.reconnecting = false;
	connectionState.reconnectAttempts = 0;
	connectionState.error = null;

	// Clear message queue and early message buffer on cleanup
	messageQueue.clear();
	earlyMessages = [];
}
